import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const getInitialLetterOfname = (fullName: string): string => {
  if (!fullName) return '';
  return fullName
    .split(' ')
    .filter(Boolean) // removes extra spaces
    .map((word) => word[0].toUpperCase())
    .join('');
};

export const getApiErrorMessage = (ApiCatchError: any): string => {
  let { message } = ApiCatchError;
  if (ApiCatchError?.data?.message === 'Payload validation error') {
    if (Array.isArray(ApiCatchError?.data?.data)) {
      message = ApiCatchError?.data?.data[0];
    }
  } else if (ApiCatchError?.data?.message) {
    message = ApiCatchError?.data?.message;
  } else if (ApiCatchError?.statusText) {
    message = ApiCatchError?.statusText;
  }
  return message;
};

export const prepareAndGetMediaURL = (path: string | null): string => {
  if (!path) return '';

  return `${process.env.REACT_APP_API_BASE_URL}/${path.replace(/^\/+|\/+$/g, '')}`;
};

export const getFormattedPrice = (amount: string | number | null | undefined) => {
  // Try converting input to number
  const number = Number(amount);

  // If invalid or not a finite number, return ₹0.00
  if (Number.isNaN(number) || !Number.isFinite(number)) {
    return '₹0';
  }

  // Format number as Indian Rupees
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number);
};

export const parseIntoNumber = (value: string | number | null | undefined): number => {
  if (Number.isNaN(value) || !value) {
    return 0;
  }
  return Number(value);
};

export const getFormattedQuantity = (qty: number | null | undefined, unit?: string): string => {
  const parsedQty = parseIntoNumber(qty);
  return unit ? `${parsedQty} ${unit}` : parsedQty.toString();
};

export const getFormattedDate = (dateStr: string | Date | undefined): string => {
  if (!dateStr) return 'Unknown';
  // return dayjs(dateStr).format('DD-MM-YYYY hh:mm A');
  return dayjs.utc(dateStr).local().format('DD-MM-YYYY hh:mm A');
  // return dayjs(dateStr).format('DD-MM-YYYY hh:mm A');
};

export const getUTCTimeStamp = (utcTimeStamp: string | null = null) => {
  if (!utcTimeStamp) {
    return dayjs().utc();
  }
  return dayjs.utc(utcTimeStamp);
};

export const getFormattedEmptyAmountWithUnit = (unit: string | undefined | null): string => {
  if (!unit) return '';
  return `Rs/${unit}`;
};

export const getPublicIP = async () => {
  try {
    const res = await fetch('https://api.ipify.org?format=json');
    const data = await res.json();
    return data.ip;
  } catch (err) {
    console.error('==========Error fetching IP:', err);
    return 'unknown';
  }
};

export const getBrowserNetworkInfo = async () => {
  return {
    ip_address: await getPublicIP(),
    agent: navigator.userAgent,
  };
};

export const debounce = (fn: any, delay: number) => {
  let timeoutId: any;
  return (...args: any) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

export const isAdminUser = (user: any): boolean => {
  return user?.user_type === 'admin';
};

export const userIsBuyer = (user: any): boolean => {
  return user?.user_type === 'buyer';
};

export const userIsSeller = (user: any): boolean => {
  return user?.user_type === 'seller';
};

export const isAdminRoute = (pathname: string): boolean => {
  return pathname.startsWith('/admin') || pathname.startsWith('admin');
};

export const isMyAccountRoute = (pathname: string): boolean => {
  return pathname.startsWith('/my-account') || pathname.startsWith('my-account');
};

export const parseQueryStringToFilters = (search: string): Record<string, any> => {
  const sp = new URLSearchParams(search);
  const out: Record<string, any> = {};
  sp.forEach((v, k) => {
    if (v === '') {
      out[k] = '';
      return;
    }
    out[k] = !Number.isNaN(Number(v)) && v.trim() !== '' ? Number(v) : v;
  });
  return out;
};

export const buildQueryStringFromFilters = (filters: Record<string, any>) => {
  const sp = new URLSearchParams();
  filters.forEach((v: any, k: string) => {
    if (v === null || v === '') {
      return;
    }
    sp.set(k, String(v));
  });
  return sp.toString();
};

export const isAuctionLive = (start_time: string | null, end_time: string | null): boolean => {
  const now = getUTCTimeStamp();
  const auctionStartTime = getUTCTimeStamp(start_time);
  const auctionEndTime = getUTCTimeStamp(end_time);
  if (now.isBetween(auctionStartTime, auctionEndTime, null, '[]')) {
    return true;
  }
  return false;
};

export const isAuctionFuture = (start_time: string | null): boolean => {
  const now = getUTCTimeStamp();
  const auctionStartTime = getUTCTimeStamp(start_time);
  if (now.isBefore(auctionStartTime)) {
    return true;
  }
  return false;
};

export default { getInitialLetterOfname };
